import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FamiliesDetailsTable } from './families-details-table/families-details-table.component';
import {
	EngagementStatusEnum,
	EngagementSurveyDto,
	FamilyWithSurveyDto,
	SurveyAnswerDto,
	SurveyStatusEnum,
	SurveyTemplateDto,
} from '@families-link/shared';
import { useMuiModal } from '../../../_core/hooks';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { useSnackbar } from 'notistack';
import {
	CreateSurveyFormModal,
	CreateSurveyFormModalProps,
} from '../../../survey/components/survey-tracking/modal-foms/create-survey-form.component';
import { ResultsSurvey } from '../../../survey/components/survey-tracking/survey-results.component';
import { SortingState } from '@tanstack/react-table';
import { SurveyRoutes } from '../../../survey/constants';
import { generatePath } from 'react-router-dom';
import { useOpenConfirmModal } from '../../hooks/modal-forms/use-open-confirm-modal-form.hook';
import {
	useCreateEngagementSurveyLinguistLinkMutation,
	useCreateEngagementSurveyMutation,
	useDeleteEngagementSurveyMutation,
	useDeleteSurveyAnswerMutation,
	useSendSurveyByEmailMutation,
	useSendSurveyByPreferedMethodMutation,
	useSendSurveyBySmsMutation,
	useSendSurveyToFamiliesMutation,
} from '../../../../store/api/survey.api';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import { QrCodeModal, QrCodeModalProps } from '../modal-qrcode-form/modal-qr-code-form.component';
import { QrCodeIcon } from '../../../_core/components/icons';
import {
	AttachFamilyModal,
	AttachFamilyModalProps,
} from '../modal-attach-family-form/modal-attach-family-form.component';
import { UnrecognizedAnswersComponent } from './unrecognized-answers.component';
import { skipToken } from '@reduxjs/toolkit/query';

interface FamilyDetailsListProps {
	engagementId: string;
	engagementStatus: string;
	engagementLanguage?: string;
	itemsList?: FamilyWithSurveyDto[];
	engagementSurvey?: EngagementSurveyDto;
	isFetching: boolean;
	surveyTemplatesList?: SurveyTemplateDto[];
	sortingArray: SortingState;
	handleSortingChange: any;
	unrecognizedAnswers?: SurveyAnswerDto[];
	isLinguistLink: boolean;
}

export const FamilyDetailsList: FC<FamilyDetailsListProps> = (props) => {
	const {
		engagementId,
		engagementStatus,
		engagementLanguage,
		isFetching,
		itemsList = [],
		engagementSurvey,
		surveyTemplatesList,
		sortingArray,
		handleSortingChange,
		unrecognizedAnswers,
		isLinguistLink,
	} = props;

	const disabledStatuses = [
		SurveyStatusEnum.DeliveredEmail,
		SurveyStatusEnum.DeliveredEmailAndSms,
		SurveyStatusEnum.DeliveredSms,
		SurveyStatusEnum.Completed,
	];
	const [t] = useTranslation();
	const [resultComponent, setResultComponent] = useState<'unrecognized' | 'recognized' | null>(null);
	const [currentFamilyWithSurvey, setCurrentFamilyWithSurvey] = useState<FamilyWithSurveyDto>();
	const [currentUnrecognizedAnswer, setCurrentUnrecognizedAnswer] = useState<SurveyAnswerDto>();
	const { enqueueSnackbar } = useSnackbar();
	const isFirstRender = useRef(true);

	const showToast = (message: string, variant: 'error' | 'default' | 'success' | 'warning' | 'info' | undefined) => {
		enqueueSnackbar(message, {
			variant,
		});
	};

	const returnFromResults = () => {
		setResultComponent(null);
	};

	const { openModal, closeModal, updateCurrentModal } = useMuiModal<CreateSurveyFormModalProps>(CreateSurveyFormModal);
	
	const [createEngagementSurveyMutation] = useCreateEngagementSurveyMutation();
	const [createEngagementSurveyLinguistLinkMutation] = useCreateEngagementSurveyLinguistLinkMutation();

	const createEngagementSurvey = isLinguistLink
		? createEngagementSurveyLinguistLinkMutation
		: createEngagementSurveyMutation;
	const handleSubmitCreate = async (surveyTemplateId: string): Promise<void> => {
		try {
			await createEngagementSurvey({
				surveyTemplateId,
				engagementId,
			}).unwrap();
			showToast('Survey for this engagement was successfully created', 'success');
			closeModal();
		} catch (error: any) {
			updateCurrentModal({ formError: error });
		}
	};
	const handleOpenModalCreate = useCallback(() => {
		openModal({
			surveyTemplatesList,
			submitAction: handleSubmitCreate,
			closeModal,
		});
	}, [openModal, closeModal, surveyTemplatesList, handleSubmitCreate]);

	const [sendSurveyToFamilies] = useSendSurveyToFamiliesMutation();
	const handleSubmitSend = async (): Promise<void> => {
		try {
			if (!engagementSurvey?.surveyTemplate) {
				showToast(`This survey was deleted from system, you can't send it to family`, 'error');

				return;
			}
			await sendSurveyToFamilies({
				engagementId,
			}).unwrap();
			showToast('Survey was successfully sent to families', 'success');
		} catch (error: any) {
			console.log(error);
			showToast('Survey sending was failed', 'error');
		}
	};

	const [deleteEngagementSurvey] = useDeleteEngagementSurveyMutation();
	const featchActionDisconnect = async () => {
		if (engagementSurvey) {
			await deleteEngagementSurvey({ id: engagementSurvey._id }).unwrap();
		}
	};
	const handleOpenDisconnectConfirmModal = useOpenConfirmModal({
		featchAction: featchActionDisconnect,
		title: t('surveys.forms.disconnect-title'),
		description: t('surveys.forms.disconnect-description'),
		submitButtonName: t('surveys.forms.buttons.disconnect'),
		toastText: 'Survey was successfully disconnected',
	});

	const [sendSurveyByEmail] = useSendSurveyByEmailMutation();
	const handleOnSendEmail = async (familyWithSurvey: FamilyWithSurveyDto) => {
		try {
			if (!familyWithSurvey?.surveyTemplate) {
				showToast(`This survey was deleted from system, you can't send it to family`, 'error');

				return;
			}
			await sendSurveyByEmail({
				email: familyWithSurvey.family.email ?? '',
				surveyId: familyWithSurvey.survey?._id,
			}).unwrap();
			showToast('Survey was successfully sent to family by email', 'success');
		} catch (error) {
			showToast('Survey sending was failed', 'error');
		}
	};

	const [sendSurveyBySms] = useSendSurveyBySmsMutation();
	const handleOnSendSms = async (familyWithSurvey: FamilyWithSurveyDto) => {
		try {
			if (!familyWithSurvey?.surveyTemplate) {
				showToast(`This survey was deleted from system, you can't send it to family`, 'error');

				return;
			}
			await sendSurveyBySms({
				phoneNumber: familyWithSurvey.family.phoneNumber,
				surveyId: familyWithSurvey.survey?._id,
			}).unwrap();
			showToast('Survey was successfully sent to family by sms', 'success');
		} catch (error) {
			showToast('Survey sending was failed', 'error');
		}
	};

	const [sendSurveyByPreferedMethod] = useSendSurveyByPreferedMethodMutation();
	const handleSendByPreferedMethod = async (familyWithSurvey?: FamilyWithSurveyDto) => {
		try {
			if (!familyWithSurvey?.surveyTemplate) {
				showToast(`This survey was deleted from system, you can't send it to family`, 'error');

				return;
			}
			await sendSurveyByPreferedMethod({
				familyId: familyWithSurvey?.family._id,
				surveyId: familyWithSurvey?.survey?._id,
			}).unwrap();
			showToast('Survey was successfully sent to family by prefered method', 'success');
		} catch (error) {
			showToast('Survey sending was failed', 'error');
		}
	};

	const handleOnManualEditing = async (familyWithSurvey?: FamilyWithSurveyDto) => {
		try {
			const surveyLink = generatePath(SurveyRoutes.ManualEditing, {
				surveyId: familyWithSurvey?.survey?._id,
				familyId: familyWithSurvey?.family?._id,
			});
			window.open(surveyLink, '_blank');
		} catch (error) {
			console.log(error);
		}
	};

	const handleOnView = async (familyWithSurvey: FamilyWithSurveyDto) => {
		setCurrentFamilyWithSurvey(familyWithSurvey);
		setResultComponent('recognized');
	};

	const { openModal: openAttachFamilyModal, closeModal: closeAttachFamilyModal } =
		useMuiModal<AttachFamilyModalProps>(AttachFamilyModal);
	const handleOpenModalAttachFamily = (data: SurveyAnswerDto) => {
		openAttachFamilyModal({
			surveyAnswer: data,
			closeModal: closeAttachFamilyModal,
			familiesWithSurveyList: itemsList,
		});
	};

	const [deleteSurveyAnswer] = useDeleteSurveyAnswerMutation();
	const featchActionSurveyAnswerDelete = async (data: SurveyAnswerDto) => {
		await deleteSurveyAnswer({ surveyAnswerId: data._id }).unwrap();
	};
	const handleOpenDeleteSurveyAnswerConfirmModal = useOpenConfirmModal({
		featchAction: featchActionSurveyAnswerDelete,
		additionalWork: returnFromResults,
		title: t('surveys.forms.delete-answer-title'),
		description: t('surveys.forms.delete-answer-description'),
		submitButtonName: t('surveys.forms.buttons.delete'),
		toastText: 'Survey answer was successfully deleted',
		modalWidth: 600,
	});

	const { openModal: openQrCodeModal, updateCurrentModal: updateQrCodeModal } =
		useMuiModal<QrCodeModalProps>(QrCodeModal);

	const handleOpenModalQrCode = useCallback(() => {
		if (engagementSurvey && engagementSurvey._id) {
			openQrCodeModal({
				survey: engagementSurvey,
			});
		}
	}, [openQrCodeModal, engagementSurvey, handleSubmitCreate]);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;

			return;
		}
		updateQrCodeModal({ survey: engagementSurvey });
	}, [engagementSurvey?.qrCodeId]);

	useEffect(() => {
		if (!resultComponent) {
			setCurrentFamilyWithSurvey(undefined);
			setCurrentUnrecognizedAnswer(undefined);
		}
	}, [resultComponent]);

	return (
		<Box sx={{ height: '100%' }}>
			{resultComponent ? (
				<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '8px' }}>
					<Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', gap: '24px', p: '24px', flex: 1 }}>
						<ResultsSurvey
							resultComponentStatus={resultComponent}
							surveyAnswer={currentUnrecognizedAnswer}
							currentFamilyWithSurvey={currentFamilyWithSurvey}
							currentEngagementId={engagementId}
							handleOpenModalCreate={handleOpenModalCreate}
							handleSubmitSend={handleSendByPreferedMethod}
							handleOnManualEditing={handleOnManualEditing}
							closeResultsComponent={returnFromResults}
							handleDeleteSurveyAnswer={handleOpenDeleteSurveyAnswerConfirmModal}
							handleAttachSurveyAnswerToFamily={handleOpenModalAttachFamily}
						/>
					</Paper>
				</Box>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '8px' }}>
					<UnrecognizedAnswersComponent
						engagementSurvey={engagementSurvey}
						handleOpenDeleteSurveyAnswerConfirmModal={handleOpenDeleteSurveyAnswerConfirmModal}
						handleOpenModalAttachFamily={handleOpenModalAttachFamily}
						setCurrentUnrecognizedAnswer={setCurrentUnrecognizedAnswer}
						setResultComponent={setResultComponent}
					/>

					<Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', gap: '24px', p: '24px', flex: 1 }}>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								width: '100%',
							}}
						>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 500, fontSize: '16px' }}>
									{t('families.family-contact')}
								</Typography>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Box sx={{ ml: 'auto', mr: 1 }}>
									{!engagementSurvey ? (
										<CoreButton
											tooltipTitle={t('buttons.tooltips.connect-survey')}
											circle
											disabled={engagementStatus === EngagementStatusEnum.Incomplete || !engagementLanguage}
											onClick={handleOpenModalCreate}
										>
											<LinkOutlinedIcon />
										</CoreButton>
									) : (
										<CoreButton
											tooltipTitle={t('buttons.tooltips.disconnect-survey')}
											circle
											onClick={handleOpenDisconnectConfirmModal}
											disabled={
												(unrecognizedAnswers && unrecognizedAnswers?.length > 0) ||
												itemsList?.filter((familyWithSurvey: FamilyWithSurveyDto) =>
													disabledStatuses.includes(familyWithSurvey.surveyStatus)
												)?.length > 0
											}
										>
											<LinkOffOutlinedIcon />
										</CoreButton>
									)}
								</Box>
								<Box sx={{ mr: 1 }}>
									<CoreButton
										tooltipTitle={t('buttons.tooltips.qr-code')}
										circle
										variant="info-light"
										disabled={!engagementSurvey}
										onClick={handleOpenModalQrCode}
									>
										<QrCodeIcon />
									</CoreButton>
								</Box>
								<Box>
									<CoreButton
										onClick={handleSubmitSend}
										disabled={
											itemsList?.filter(
												(familyWithSurvey: FamilyWithSurveyDto) =>
													familyWithSurvey.surveyStatus === SurveyStatusEnum.NotShared
											)?.length === 0
										}
										sx={{ py: '8px' }}
									>
										Send Survey
									</CoreButton>
								</Box>
							</Box>
						</Box>

						<FamiliesDetailsTable
							data={itemsList ?? []}
							loading={isFetching}
							onSendEmail={handleOnSendEmail}
							onSendSms={handleOnSendSms}
							onEdit={handleOnManualEditing}
							onView={handleOnView}
							sorting={sortingArray}
							onSortingChange={handleSortingChange}
						/>
					</Paper>
				</Box>
			)}
		</Box>
	);
};
